import { useMemo, useState, useRef, useCallback } from "react";
import { Box, Flex } from "@chakra-ui/layout";
import DefaultButton from "components/Button/PrimaryButton";
import Container from "components/Container";
import { useLoading } from "components/GlobalLoading";
import SelectTipoConsumo from "components/Input/SelectTipoConsumo";
import PageHeader from "components/Page/PageHeader";
import { useConsumo } from "core/features/consumo/hooks/useConsumo";
import { useConsumoDashboard } from "core/features/consumo/hooks/useConsumoDashboard";
import useConsumoUnidadesInfiniteScroll from "core/features/consumo/hooks/useConsumoUnidadesInfiniteScroll";
import { ConsumoUnidade, TipoConsumoEnum } from "core/features/consumo/typings";
import moment from "moment";
import InfiniteScrollCardList from "ui/components/InfiniteScrollCardList";
import SelectMonthYear from "ui/components/Input/SelectMonthYear";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import useFiltroConsumo from "../../hooks/useFiltroConsumo";
import CardUnidade from "./CardUnidade";
import DetalhesConsumo from "./DetalhesConsumo/index";
import ModalEscolherTipo from "./ModalEscolherTipo/index";
import ModalImportarExcelConsumo from "./ModalImportarExcel/index";
import colors from "core/resources/theme/colors";
import ModalAlterarUnidadeMedida from "./ModalAlterarUnidadeMedida/index";
import useSaveTenantTipoConsumo from "core/features/consumo/hooks/useSaveTenantTipoConsumo";
import { showSuccess } from "infra/helpers/alerta";
import usePdfConsumoUnidades from "core/features/consumo/hooks/usePdfConsumoUnidades";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import PDFExportarConsumo from "../PDFExportarConsumo/index";
import ExportMenu from "components/ExportMenu";
import { exportExcelPdfConsumo } from "../../util/index";

export const mapTipoConsumo: Record<TipoConsumoEnum, string> = {
  [TipoConsumoEnum.Agua]: "Água",
  [TipoConsumoEnum.Gas]: "Gás",
  [TipoConsumoEnum.Energia]: "Energia",
  [TipoConsumoEnum.AguaMineral]: "Água Mineral",
  [TipoConsumoEnum.Esgoto]: "Esgoto",
};

export default function ListarConsumo() {
  const { filtro, onChangeFiltro } = useFiltroConsumo();
  const [isOpenModalEscolherTipo, setIsOpenModalEscolherTipo] = useState(true);
  const [isOpenModalImportar, setIsOpenModalImportar] = useState(false);
  const [isOpenModalAlterarUnidadeMedida, setIsOpenModalAlterarUnidadeMedida] =
    useState(false);

  const [editableUnidades, setEditableUnidades] = useState(false);

  const { data: consumo, isLoading, isFetching, refetch } = useConsumo(filtro);

  const {
    data: dashboard,
    isLoading: isLoadingDashboard,
    isFetching: isFetchingDashboard,
    refetch: refetchDashboard,
  } = useConsumoDashboard(consumo?.id);

  const {
    data: unidades,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingUnidades,
    refetch: refetchUnidades,
    isRefetching,
  } = useConsumoUnidadesInfiniteScroll(consumo?.id, {
    search: filtro.search,
    consumoAnteriorId: consumo?.consumoAnteriorId,
  });

  const { refetch: refetchPdf, isFetching: isFetchingPdf } =
    usePdfConsumoUnidades(consumo?.id, {
      search: filtro.search,
      consumoAnteriorId: consumo?.consumoAnteriorId,
    });

  const {
    mutate: saveTenantTipoConsumo,
    isLoading: isLoadingSaveTenantTipoConsumo,
  } = useSaveTenantTipoConsumo();

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: consumo?.tipoConsumoId
      ? `Relatório de Consumo de ${mapTipoConsumo[consumo?.tipoConsumoId]}.pdf`
      : "Relatório de Consumo.pdf",
    Component: PDFExportarConsumo,
    loading: isFetchingPdf,
  });

  const _onExportPdf = useCallback(async () => {
    if (!consumo) return;

    const { data: pdfConsumoData } = await refetchPdf();

    if (pdfConsumoData) {
      onExportPdf({
        ...pdfConsumoData,
        consumo,
        dashboard,
        unidadeMedida: consumo?.unidadeMedida,
        filtro,
      });
    }
  }, [refetchPdf, consumo, onExportPdf, dashboard, filtro]);

  const _onExportExcel = useCallback(async () => {
    if (!consumo || !dashboard) return;

    const { data: pdfConsumoData } = await refetchPdf();

    exportExcelPdfConsumo({
      ...pdfConsumoData!,
      consumo,
      dashboard,
      filtro,
      unidadeMedida: consumo?.unidadeMedida,
    });
  }, [refetchPdf, consumo, dashboard, filtro]);

  const memoChangeMedidas = useRef<Record<string, number>>({});

  useLoading(
    isLoading ||
      isLoadingUnidades ||
      isFetching ||
      (isRefetching && !isFetchingNextPage) ||
      isFetchingPdf
  );

  const items = useMemo(() => {
    return (
      unidades?.pages.reduce(
        (agg: ConsumoUnidade[], page) => agg.concat(page.items ?? []),
        []
      ) ?? []
    );
  }, [unidades]);

  const refetchAll = () => {
    refetch().then(() => {
      refetchDashboard();
      refetchUnidades();
      memoChangeMedidas.current = {};
    });
  };

  const isConsumoAlteravel =
    consumo &&
    [
      TipoConsumoEnum.Agua,
      TipoConsumoEnum.AguaMineral,
      TipoConsumoEnum.Esgoto,
    ].includes(consumo.tipoConsumoId);

  return (
    <Container>
      <PageHeader
        title={
          filtro.tipoConsumoId
            ? `Consumo de ${mapTipoConsumo[filtro.tipoConsumoId]}`
            : "Consumo de água, esgoto, energia e gás"
        }
        extraButtonOnClick={
          isConsumoAlteravel
            ? () => setIsOpenModalAlterarUnidadeMedida(true)
            : undefined
        }
        extraButtonTitle={
          isConsumoAlteravel ? "Alterar unidade de medida" : undefined
        }
        midComponent={
          <Flex gap={4}>
            <SelectMonthYear
              bg="white"
              value={filtro.mes ? moment(filtro.mes, "YYYY-MM") : undefined}
              onChange={(date) =>
                onChangeFiltro({ mes: date?.format("YYYY-MM") })
              }
              placeholder="Selecione um mês"
            />
            <Box minWidth={200}>
              <SelectTipoConsumo
                value={filtro.tipoConsumoId}
                onChange={(tipoConsumoId) => onChangeFiltro({ tipoConsumoId })}
                allowClear={false}
                className="antd-select-white-bg antd-select-force-white-bg ant-select-customize-input"
              />
            </Box>
          </Flex>
        }
      />

      <DetalhesConsumo
        consumo={consumo}
        dashboard={dashboard}
        isLoading={isLoading || isFetching}
        isLoadingDashboard={isLoadingDashboard || isFetchingDashboard}
        refetch={refetch}
      />

      <Box my={4}>
        <ListingPageHeaderBar
          valueSearch={filtro.search}
          onSearch={(search?: string) => onChangeFiltro({ search })}
          children={
            consumo ? (
              <Flex gap={4} flex={1}>
                <DefaultButton
                  onClick={() => setEditableUnidades((state) => !state)}
                >
                  {editableUnidades ? "Parar de editar" : "Editar unidades"}
                </DefaultButton>
                <DefaultButton
                  bgColor={colors.green500}
                  onClick={() => setIsOpenModalImportar(true)}
                >
                  Importar Excel
                </DefaultButton>

                <Flex marginLeft="auto">
                  <ExportMenu
                    onExportPdf={_onExportPdf}
                    onExportarXlsx={_onExportExcel}
                  />
                </Flex>
              </Flex>
            ) : null
          }
        />
      </Box>

      {consumo && !isLoading && !isFetching ? (
        <Box mt={4} paddingBottom={8}>
          <InfiniteScrollCardList
            isLoading={isLoadingUnidades || isLoading || isFetching}
            isFetchingNextPage={isFetchingNextPage}
            data={items}
            hasMore={!!hasNextPage}
            onNext={fetchNextPage}
            emptyDescription="Sem unidades para esse filtro"
            defaultCardSize={88}
            key={consumo.id}
            RenderCard={({ item, index }) => (
              <CardUnidade
                consumo={consumo}
                unidade={item}
                editable={editableUnidades}
                key={`${consumo.id}-${item.unidadeId}`}
                defaultValue={
                  memoChangeMedidas.current[`${consumo.id}-${item.unidadeId}`]
                }
                onChangeMedida={(v) => {
                  memoChangeMedidas.current[`${consumo.id}-${item.unidadeId}`] =
                    v;
                }}
              />
            )}
          />
        </Box>
      ) : null}

      {consumo && isConsumoAlteravel ? (
        <ModalAlterarUnidadeMedida
          open={isOpenModalAlterarUnidadeMedida}
          onClose={() => setIsOpenModalAlterarUnidadeMedida(false)}
          onSave={(unidadeMedida) => {
            saveTenantTipoConsumo(
              {
                tipoConsumoId: consumo.tipoConsumoId,
                unidadeMedida,
              },
              {
                onSuccess: () => {
                  showSuccess().then(() => {
                    setIsOpenModalAlterarUnidadeMedida(false);
                    refetch();
                  });
                },
              }
            );
          }}
          isLoading={isLoadingSaveTenantTipoConsumo}
          defaultUnidadeMedida={consumo.unidadeMedida}
          tipoConsumoId={consumo.tipoConsumoId}
        />
      ) : null}

      <ModalEscolherTipo
        open={isOpenModalEscolherTipo}
        onClose={() => setIsOpenModalEscolherTipo(false)}
        onSave={(tipoConsumoId, mes) => {
          onChangeFiltro({ tipoConsumoId, mes });
          setIsOpenModalEscolherTipo(false);
        }}
      />

      <ModalImportarExcelConsumo
        open={isOpenModalImportar}
        onClose={() => setIsOpenModalImportar(false)}
        id={consumo?.id}
        refetchList={refetchAll}
      />
    </Container>
  );
}
